var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j;
import { once } from "@play-co/native-game-js/utils";
import { analytics } from "../../analytics/index.mjs";
import { retry } from "../../asyncUtils.mjs";
import { createError } from "../../errorUtils.mjs";
import { productFromFacebookData } from "../../Product.mjs";
import { trackNotificationRegistration, trackNotificationRegistrationSuccess, trackNotificationRegistrationFailure } from "../events.mjs";
function assertAvailable(self) {
  if (!self.isAvailable) {
    throw new Error("API not available");
  }
}
class NativeBuilderInterface {
  constructor() {
    __publicField(this, "interface", "native-builder");
    __publicField(this, "platform", {
      get os() {
        var _a2;
        return (_a2 = window.TLNative) == null ? void 0 : _a2.Settings.os;
      },
      get version() {
        var _a2;
        return (_a2 = window.TLNative) == null ? void 0 : _a2.Settings.nativeVersion;
      },
      get locale() {
        var _a2;
        return (_a2 = window.TLNative) == null ? void 0 : _a2.Settings.locale;
      }
    });
    __publicField(this, "app", {
      get version() {
        var _a2, _b2;
        return (_b2 = (_a2 = window.Native) == null ? void 0 : _a2.app) == null ? void 0 : _b2.version;
      }
    });
    __publicField(this, "amplitude", {
      async getDeviceId() {
        var _a2, _b2;
        return (_b2 = (_a2 = window.Native) == null ? void 0 : _a2.amplitude) == null ? void 0 : _b2.deviceId;
      }
    });
    __publicField(this, "attribution", ((_d = (_c = (_b = (_a = window.Native) == null ? void 0 : _a.attribution) == null ? void 0 : _b.results) == null ? void 0 : _c.data) == null ? void 0 : _d.payload) ? Promise.resolve(window.Native.attribution.results.data.payload) : void 0);
    __publicField(this, "game", {
      show() {
        var _a2, _b2;
        (_b2 = (_a2 = window.TLNative) == null ? void 0 : _a2.core) == null ? void 0 : _b2.notifyLoaded();
      },
      reload(loginWithLine) {
        var _a2, _b2, _c2;
        (_c2 = (_b2 = (_a2 = window.TLNative) == null ? void 0 : _a2.core) == null ? void 0 : _b2.restart) == null ? void 0 : _c2.call(_b2, loginWithLine);
      }
    });
    __publicField(this, "links", new class {
      constructor() {
        __publicField(this, "_firstOpenedPromise", (_g = (_f = (_e = window.Native) == null ? void 0 : _e.links) == null ? void 0 : _f.getEvents) == null ? void 0 : _g.call(_f).then((links) => {
          var _a2;
          return (_a2 = links.find((link) => link.appState === "closed")) == null ? void 0 : _a2.url;
        }));
      }
      get isAvailable() {
        var _a2, _b2;
        return typeof ((_b2 = (_a2 = window.Native) == null ? void 0 : _a2.links) == null ? void 0 : _b2.getEvents) === "function";
      }
      async firstOpened() {
        assertAvailable(this);
        return this._firstOpenedPromise;
      }
      async list() {
        assertAvailable(this);
        const links = await window.Native.links.getEvents();
        return links.map(({ url }) => url);
      }
      async remove(url) {
      }
      subscribe(callback) {
        assertAvailable(this);
        window.Native.links.addEventListener((link) => {
          callback(link.url);
        });
      }
    }());
    __publicField(this, "auth", new class {
      get isAvailable() {
        var _a2, _b2;
        return typeof ((_b2 = (_a2 = window.Native) == null ? void 0 : _a2.login) == null ? void 0 : _b2.getSession) === "function";
      }
      async fetch() {
        assertAvailable(this);
        return window.Native.login.getSession();
      }
      async logout() {
        assertAvailable(this);
        window.Native.login.logout();
      }
    }());
    __publicField(this, "notifications", new class {
      constructor() {
        __publicField(this, "_firstOpenedPromise", (_j = (_i = (_h = window.Native) == null ? void 0 : _h.pushNotifications) == null ? void 0 : _i.getAccepted) == null ? void 0 : _j.call(_i).then(
          (notifications) => notifications.find(
            (notification) => notification.appState === "closed"
          )
        ));
      }
      get isAvailable() {
        var _a2, _b2;
        return typeof ((_b2 = (_a2 = window.Native) == null ? void 0 : _a2.pushNotifications) == null ? void 0 : _b2.register) === "function";
      }
      async firstOpened() {
        assertAvailable(this);
        return this._firstOpenedPromise;
      }
      async register(payload) {
        assertAvailable(this);
        trackNotificationRegistration(payload);
        const { userAuthorization, deviceToken } = await window.Native.pushNotifications.register();
        const success = !!deviceToken && userAuthorization !== "denied";
        analytics.setUserProperties({
          isRegisteredForPushNotifications: success
        });
        success ? trackNotificationRegistrationSuccess(payload) : trackNotificationRegistrationFailure(
          createError("USER_INPUT", "Did not register for notifications"),
          payload
        );
        return {
          success,
          deviceToken: success ? deviceToken : void 0
          // if denied strip out the device token
        };
      }
      async list() {
        assertAvailable(this);
        return window.Native.pushNotifications.getAccepted();
      }
      async remove(id) {
      }
      subscribe(callback) {
        assertAvailable(this);
        window.Native.pushNotifications.addAcceptedListener(callback);
      }
    }());
    __publicField(this, "purchases", new class {
      constructor() {
        __publicField(this, "initPromise");
        __publicField(this, "catalog", []);
        __publicField(this, "init", once(async (productIds) => {
          assertAvailable(this);
          this.initPromise = (async () => {
            await retry(() => window.TLNative.Payments.initAsync(productIds));
            const { result: products } = await retry(
              () => window.TLNative.Payments.getCatalogAsync()
            );
            this.catalog = products.map(productFromFacebookData);
          })();
          await this.initPromise;
        }));
      }
      get isAvailable() {
        var _a2;
        return ((_a2 = window.TLNative) == null ? void 0 : _a2.Settings.paymentsAvailable) === "yes";
      }
      async products() {
        assertAvailable(this);
        await this.initPromise;
        return this.catalog;
      }
      async purchase(productId, payload) {
        assertAvailable(this);
        await this.initPromise;
        return window.TLNative.Payments.purchaseAsync({
          productID: productId,
          developerPayload: payload
        });
      }
      async finish(purchase) {
        assertAvailable(this);
        await this.initPromise;
        return window.TLNative.Payments.consumePurchaseAsync(
          purchase.purchaseToken
        );
      }
      async unfinished() {
        assertAvailable(this);
        await this.initPromise;
        return window.TLNative.Payments.getPurchasesAsync();
      }
    }());
    __publicField(this, "store", new class {
      get isAvailable() {
        var _a2;
        return typeof ((_a2 = window.TLNative) == null ? void 0 : _a2.RequestStoreReview) === "function";
      }
      async requestReview(_payload) {
        assertAvailable(this);
        return window.TLNative.RequestStoreReview(true);
      }
    }());
  }
  static create() {
    if (window.Native || window.TLNative) {
      return new NativeBuilderInterface();
    }
    return void 0;
  }
}
export {
  NativeBuilderInterface
};
